/**
 * Bars: grip, attach with the length ease, the pendulum with tilt and tuck
 * pumping, the pump grade, kick and plain release, and the regrab lockout.
 */

import { releaseAttitude } from "../attitude";
import { comHeight, restLength } from "../body";
import type { EventBuffer } from "../events";
import { abs, atan2, clamp, cos, RAD_TO_DEG, sign, sin, sqrt, wrapAngle } from "../fastmath";
import { emitGrade, gradeBand, gradeOffset } from "../grades";
import { linkOnAttach, linkOnAttachedTick, linkOnBottomCrossed, linkOnRelease } from "../link";
import type { SimState } from "../state";
import type { CompiledTuning } from "../tuning";
import { DT, Ev, Grade, GradeKind, HZ, type InputFrame, S, SUB_DT, SUBSTEPS } from "../types";
import type { World } from "../world";
import { apexPassed, tickLadderClimb, tryDuckClimb, tryPop } from "./ladder";
import { setState } from "./transitions";

/** Grip the nearest bar inside the reach radius of the hand, if the arms are out. */
export function tryGrip(world: World, s: SimState, t: CompiledTuning, ev: EventBuffer): boolean {
  if (s.armsOut !== 1 || s.barId >= 0 || s.reach < t.gripRelease) return false;
  const exclude = s.lastBarId >= 0 && (s.lockout > 0 || s.leftRadius === 0) ? s.lastBarId : -1;
  const bar = world.nearestBar(s.handX, s.handY, s.reachRadius, exclude, s.ladderId, s.ladderRung);
  if (bar < 0) return false;
  attach(world, s, t, ev, bar);
  return true;
}

/** A catch: the link bookkeeping, the physics of taking the bar, and the catch grade. */
export function attach(world: World, s: SimState, t: CompiledTuning, ev: EventBuffer, bar: number): void {
  linkOnAttach(s, t, ev, bar);
  attachPhysics(world, s, t, ev, bar);
  // Taking a ladder's bar: it sits on this rung until the player lets go.
  const ladder = world.barLadder[bar]!;
  s.ladderId = ladder;
  s.ladderRung = ladder >= 0 ? world.barRung[bar]! : 0;
  s.ladderChain = 0;
  s.popping = 0;
  s.ladderLiftY = 0;
  s.ladderLiftTick = 0;
  s.ladderLiftVelocity = 0;
  s.ladderCrossings = 0;
  s.ladderDuckBottom = -1;
  gradeCatch(s, t, ev, s.theta, s.omega);
}

/**
 * Take `bar` with the body where it is: the angle from the bar to the mass, the tangential part of
 * the velocity as the swing (the arms absorb the radial part), the actual distance as the pendulum's
 * length, eased to the rest length over the attach ticks. A catch and a salmon ladder pop both do this.
 */
export function attachPhysics(
  world: World,
  s: SimState,
  t: CompiledTuning,
  ev: EventBuffer,
  bar: number,
): void {
  const bx = world.barX[bar]!;
  const by = world.barY[bar]!;
  const comX = s.x;
  const comY = s.y + comHeight(t, s.tuck);
  const dx = comX - bx;
  const dy = comY - by;
  const d = sqrt(dx * dx + dy * dy);
  // Angle from straight down, positive toward +x.
  const theta = d > 1e-9 ? atan2(dx, -dy) : 0;
  const Lrest = restLength(t, s.tuck);
  const L0 = d > 1e-6 ? d : 1e-6;
  const tx = cos(theta);
  const ty = sin(theta);
  const caught = ((s.vx * tx + s.vy * ty) / L0) * t.catchRetention;

  s.barId = bar;
  s.theta = theta;
  s.omega = caught;
  s.L = L0;
  // The arms absorb any energy above the swing ceiling, so a catch cannot carry the body over the
  // top by itself (owner, 2026-09-14: a rising catch past the gym's high bar gave a giant for
  // free). Nothing on a bar exceeds maxSwingAngle now; a grip that slips is open decision 3.
  capPumpEnergy(s, t, -1e30);
  const omega = s.omega;
  s.Lrest = Lrest;
  s.attachLeft = t.attachTicks;
  s.attachTotal = t.attachTicks;
  s.attachL0 = L0;
  s.lastCrossTime = -1e9;
  s.idleTicks = 0;
  s.attachPhi0 = s.phi;
  s.barLean = 0;
  s.tumbling = 0;
  s.plantBox = -1;
  s.planted = 0;
  s.plantHands = 0;
  s.mantling = 0;
  if (abs(omega) > 0.05) s.facing = sign(omega);
  // Radial velocity is absorbed by the arms; the tangential part continues.
  const vt = omega * L0;
  s.ax = (vt * tx - s.vx) / DT;
  s.ay = (vt * ty - s.vy) / DT;
  s.vx = vt * tx;
  s.vy = vt * ty;
  s.grounded = 0;
  s.coyote = 0;
  s.buffer = 0;
  s.jumpCutArmed = 0;
  s.lastApexTime = -1e9;
  ev.push(Ev.Gripped, s.tick, bar, omega, L0);
  setState(s, ev, abs(omega) > t.hangOmega ? S.Swing : S.Hang);
}

/**
 * The catch grade (plan section 7.1): GOOD with the arms out catchSetupMs before contact, PERFECT
 * when also positioned (the mass at least catchReadyDeg behind the bar in the direction of travel)
 * and the result is a swing, OK for a late reach (a save). A catch never misses.
 */
function gradeCatch(s: SimState, t: CompiledTuning, ev: EventBuffer, theta: number, omega: number): void {
  const leadMs = (s.armsOutTicks * 1000) / HZ;
  let grade: number = Grade.Ok;
  if (s.armsOutTicks >= t.catchSetupTicks) {
    const ready = theta * s.linkDir <= -t.catchReadyRad && abs(omega) > t.hangOmega;
    grade = ready ? Grade.Perfect : Grade.Good;
  }
  s.lastCatchGrade = grade;
  s.armsOutTicks = 0;
  emitGrade(s, t, ev, GradeKind.Catch, grade, leadMs);
}

/** Swing energy per unit mass: kinetic plus potential relative to the bar. */
function swingEnergy(t: CompiledTuning, theta: number, omega: number, L: number): number {
  const v = omega * L;
  return 0.5 * v * v - t.swingG * L * cos(theta);
}

/**
 * The pump ceiling: a tick's pumping (tilt torque, the tuck shortening, the PERFECT bonus) may not
 * raise the swing's amplitude above maxSwingAngle. Amplitude, not raw energy, is what is compared:
 * energy per unit mass scales with L, so a shortening at the ceiling measured in raw energy kept
 * the long pendulum's energy on the short one and went over the top. `ampBefore` is the swing's
 * E / (g L) before the tick (-cos of its amplitude); an amplitude already past the ceiling is kept,
 * never raised. attach() trims a catch to it too, so nothing on a bar exceeds maxSwingAngle.
 */
function capPumpEnergy(s: SimState, t: CompiledTuning, ampBefore: number): void {
  if (t.swingG <= 0) return; // no gravity, no amplitude to speak of
  const ampMax = -t.cosMaxSwing;
  const amp = ampBefore > ampMax ? ampBefore : ampMax;
  const limit = amp * t.swingG * s.L;
  const e = swingEnergy(t, s.theta, s.omega, s.L);
  if (e <= limit) return;
  const v2 = 2 * (limit + t.swingG * s.L * cos(s.theta));
  const v = v2 > 0 ? sqrt(v2) : 0;
  s.omega = sign(s.omega) * (v / s.L);
}

function gradePump(s: SimState, t: CompiledTuning, ev: EventBuffer): void {
  if (abs(s.omega) < t.hangOmega && abs(s.theta) < 0.05) {
    s.lastPumpGrade = Grade.Ok;
    s.lastPumpOffsetMs = 0;
    s.lastPumpTick = s.tick;
    ev.push(Ev.Pump, s.tick, Grade.Ok, 0);
    emitGrade(s, t, ev, GradeKind.Pump, Grade.Ok, 0);
    return;
  }
  const back = s.tick - s.lastCrossTime; // ticks since the last bottom crossing
  let forward = 1e9;
  if (s.theta * s.omega < 0 && s.omega !== 0) forward = abs(s.theta / s.omega) / DT;
  let off: number;
  if (back <= forward) off = back;
  else off = -forward;
  // The tuck takes tuckInMs to shorten, so the energy peaks for a press about half that before the
  // crossing: grade the shortening's midpoint (pumpLeadMs after the press), not the press.
  const ms = (off * 1000) / HZ + t.pumpLeadMs;
  const grade = gradeOffset(ms, t.pumpPerfectMs, t.pumpGoodMs, t.pumpGoodMs);
  if (grade === Grade.Perfect) s.omega *= t.pumpBonus;
  s.lastPumpGrade = grade;
  s.lastPumpOffsetMs = ms;
  s.lastPumpTick = s.tick;
  ev.push(Ev.Pump, s.tick, grade, ms);
  emitGrade(s, t, ev, GradeKind.Pump, grade, ms);
}

export function release(
  s: SimState,
  input: InputFrame,
  t: CompiledTuning,
  ev: EventBuffer,
  kick: boolean,
): void {
  const tx = cos(s.theta);
  const ty = sin(s.theta);
  const vt = s.omega * s.L;
  let vx = vt * tx;
  let vy = vt * ty;
  if (s.ladderLiftVelocity !== 0) vy += s.ladderLiftVelocity;
  // Release grade (plan section 7.2): the angle past the bottom on the forward swing, negative on
  // the way down to it.
  const past = abs(s.theta) * RAD_TO_DEG;
  const angle = s.theta * s.omega > 0 ? past : -past;
  const grade = gradeBand(
    angle,
    t.releasePerfectMin,
    t.releasePerfectMax,
    t.releaseGoodMin,
    t.releaseGoodMax,
  );
  if (kick) {
    const dir = s.omega !== 0 ? sign(s.omega) : s.facing;
    const scale = grade === Grade.Perfect ? t.kickPerfectScale : grade === Grade.Good ? 1 : t.kickOkScale;
    const k = (t.releaseKick * (1 - s.tuck) + t.releaseKickTucked * s.tuck) * scale;
    vx += dir * k * tx;
    vy += dir * k * ty;
    vx += t.releaseNudge * input.moveX;
  }
  s.ax = (vx - s.vx) / DT;
  s.ay = (vy - s.vy) / DT;
  s.vx = vx;
  s.vy = vy;
  ev.push(Ev.Released, s.tick, kick ? 1 : 0, sqrt(vx * vx + vy * vy), s.theta * RAD_TO_DEG);
  s.lastReleaseGrade = grade;
  emitGrade(s, t, ev, GradeKind.Release, grade, angle);
  linkOnRelease(s, ev);
  releaseAttitude(s, t);
  s.lastBarId = s.barId;
  s.barId = -1;
  // Letting go of a ladder's bar drops it back onto the bottom rung.
  s.ladderId = -1;
  s.ladderRung = 0;
  s.ladderChain = 0;
  s.popping = 0;
  s.ladderLiftY = 0;
  s.ladderLiftTick = 0;
  s.ladderLiftVelocity = 0;
  s.ladderCrossings = 0;
  s.ladderDuckBottom = -1;
  s.lockout = t.regrabTicks;
  s.leftRadius = 0;
  s.ticksSinceRelease = 0;
  s.grounded = 0;
  s.coyote = 0;
  s.jumpCutArmed = 0;
  setState(s, ev, S.Fall);
}

/** One tick while attached. Returns true if the character released this tick. */
export function tickAttached(
  world: World,
  s: SimState,
  input: InputFrame,
  t: CompiledTuning,
  ev: EventBuffer,
  jumpPressed: boolean,
  tuckPressed: boolean,
  tuckReleased = false,
  pullPressed = false,
): boolean {
  // A jump pressed during hit-stop waits in the buffer and releases on the first tick after it.
  if (jumpPressed || s.buffer > 0) {
    s.buffer = 0;
    release(s, input, t, ev, true);
    return true;
  }
  if (s.reach < t.gripRelease) {
    release(s, input, t, ev, false);
    return true;
  }
  const onLadder = world.barLadder[s.barId]! >= 0;
  // Saved legacy tapes retain the original apex pull/kip. Current ladders use PERFECT ducks below.
  if (t.ladderPerfectDuck === 0) {
    if (onLadder && (pullPressed || tuckReleased)) tryPop(world, s, t, ev, tuckReleased);
    if (s.popping === 1 && s.attachLeft <= 0) s.popping = 0;
  }

  const Lrest = restLength(t, s.tuck);
  s.Lrest = Lrest;
  const ampBefore = t.swingG > 0 ? swingEnergy(t, s.theta, s.omega, s.L) / (t.swingG * s.L) : 0;
  if (s.attachLeft > 0) {
    // Ease the actual length to the rest length; the arms absorb the difference.
    s.attachLeft--;
    const f = s.attachTotal > 0 ? s.attachLeft / s.attachTotal : 0;
    s.L = Lrest + (s.attachL0 - Lrest) * f;
  } else if (s.L !== Lrest) {
    // Conservation of angular momentum on every change of length.
    const ratio = s.L / Lrest;
    s.omega *= ratio * ratio;
    s.L = Lrest;
  }

  if (tuckPressed) {
    gradePump(s, t, ev);
    if (onLadder && t.ladderPerfectDuck > 0) tryDuckClimb(world, s, t, ev);
  }
  if (onLadder && t.ladderPerfectDuck > 0) tickLadderClimb(world, s, t, ev);

  // Velocity Verlet: second order and symplectic, so the swing's energy neither drifts nor rings.
  const gOverL = t.swingG / s.L;
  // Pump input, "push the way you are swinging": the right stick's tilt, the left stick, or either
  // (pumpStick 0, 1, 2). With both, the two sticks add and saturate at a full push.
  let pumpIn: number;
  if (t.pumpStick >= 2) pumpIn = clamp(input.tiltX + input.moveX, -1, 1);
  else if (t.pumpStick >= 1) pumpIn = input.moveX;
  else pumpIn = input.tiltX;
  const pump = t.pumpGain * pumpIn;
  // Doing nothing on the bar deadens the swing: after swingIdleAfterMs with no push, tuck, or pull
  // the damping ramps from swingDamping up to swingIdleDamping over the same time again, so an
  // untouched swing settles to a hang in a couple of seconds instead of ringing for twenty.
  if (pumpIn !== 0 || input.tuck || input.pull > 0) s.idleTicks = 0;
  else if (s.idleTicks < 1e9) s.idleTicks++;
  const idleSpan = t.swingIdleAfterTicks > 0 ? t.swingIdleAfterTicks : 1;
  const idle = clamp((s.idleTicks - t.swingIdleAfterTicks) / idleSpan, 0, 1);
  const damp = t.swingDamping + (t.swingIdleDamping - t.swingDamping) * idle;
  for (let sub = 0; sub < SUBSTEPS; sub++) {
    const th0 = s.theta;
    const omega0 = s.omega;
    const a0 = -gOverL * sin(th0) - damp * s.omega + pump;
    s.theta = th0 + s.omega * SUB_DT + 0.5 * a0 * SUB_DT * SUB_DT;
    const omegaMid = s.omega + a0 * SUB_DT;
    const a1 = -gOverL * sin(s.theta) - damp * omegaMid + pump;
    s.omega += 0.5 * (a0 + a1) * SUB_DT;
    if ((th0 < 0 && s.theta >= 0) || (th0 > 0 && s.theta <= 0)) {
      s.lastCrossTime = s.tick - 1 + (sub + 1) / SUBSTEPS;
      if (onLadder && t.ladderPerfectDuck > 0) s.ladderCrossings++;
      ev.push(Ev.BottomCrossed, s.tick, sign(s.omega));
      linkOnBottomCrossed(s, ev, sign(s.omega), onLadder);
    }
    // The apex: the swing turns round (a real swing, not the wobble of a hang).
    if (omega0 * s.omega < 0 && abs(s.theta) > 0.05) {
      s.lastApexTime = s.tick - 1 + (sub + 1) / SUBSTEPS;
      if (onLadder && t.ladderPerfectDuck === 0) apexPassed(s);
    }
  }
  // Keep the angle bounded if a fast catch ever carries the body over the top.
  s.theta = wrapAngle(s.theta);
  capPumpEnergy(s, t, ampBefore);

  const st = sin(s.theta);
  const ct = cos(s.theta);
  const bx = world.barX[s.barId]!;
  const by = world.barY[s.barId]! + s.ladderLiftY;
  s.x = bx + s.L * st;
  s.y = by - s.L * ct - comHeight(t, s.tuck);
  const vt = s.omega * s.L;
  const vx = vt * ct;
  const vy = s.ladderLiftVelocity === 0 ? vt * st : vt * st + s.ladderLiftVelocity;
  s.ax = (vx - s.vx) / DT;
  s.ay = (vy - s.vy) / DT;
  s.vx = vx;
  s.vy = vy;

  linkOnAttachedTick(s, t, ev);
  if (s.state === S.Swing) {
    if (abs(s.omega) < t.hangOmega && abs(s.theta) < 0.15) setState(s, ev, S.Hang);
  } else if (abs(s.omega) >= t.hangOmega) {
    setState(s, ev, S.Swing);
  }
  return false;
}

/** Track the regrab lockout while not attached. */
export function tickLockout(world: World, s: SimState): void {
  if (s.lastBarId < 0) return;
  if (s.lockout > 0) s.lockout--;
  if (s.leftRadius === 0) {
    const dx = world.barX[s.lastBarId]! - s.handX;
    const dy = world.barY[s.lastBarId]! - s.handY;
    if (dx * dx + dy * dy > s.reachRadius * s.reachRadius) s.leftRadius = 1;
  }
}
